/*------------------------------------------------------------------------------
   About      : FE-owned registry of gated Pages (nav drawer entries) + nested
                 CTAs (RBAC v2)

   This is a compile-time, frontend-only catalog — never synced or mirrored to
   the backend. Extending it is a code change: add the page/CTA here, then
   reference `UI_PAGES.X.id` from a RouteEntry.uiPageId or a PermissionGate.

   Hierarchy: section (drawer area) → page (nav item) → CTAs (user-facing
   actions, labeled exactly as shown in the UI). Each page/CTA lists the
   proxypulse API actions (`apis`) it depends on — referenced from
   BffFeatureCatalog so a string still appears in the codebase exactly once.
   `apis` is metadata for the Access Control panel (grant visibility +
   capability together, spot "page granted but API missing" gaps); runtime
   gating stays AND-composed and per-id. Strapi-direct pages (config/string
   registry) have no `apis` — their CTAs are granted as plain ui.* ids and
   Strapi-side enforcement stays the legacy role handling.

   Ids are immutable once shipped (`ui.page.<page>` / `ui.cta.<page>.<cta>`,
   snake_case) — renaming orphans any grants already made against the old id.
   Add a new id + deprecate the old one instead of renaming.

   `enforced: false` means the page is grantable in the admin panel (so grants
   can be pre-provisioned) but not yet gated at runtime — flip to `true` in a
   follow-up PR only after confirming the relevant roles have been granted.
   Super-admin-flagged users bypass all of this via AccessInfo.getIsSuperAdmin
   (short-circuits `can()` itself, so every id-space is covered).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { BFF_FEATURES, BffFeatureAction } from './BffFeatureCatalog'

/** A backend-enforced proxypulse action a page/CTA depends on. */
export type UiApiAction = BffFeatureAction

export interface UiCtaDef {
  id    : string
  /** Exactly the label the user sees on the control (e.g. "Save String"). */
  label : string
  /** Backend actions this CTA calls — grant these alongside the CTA. */
  apis ?: readonly UiApiAction[]
}

export type UiSection = 'ADMIN' | 'PROJECT' | 'CMS' | 'CONFIG_TOOLS'

/** Display names for the admin panels that render the registry by section. */
export const UI_SECTION_LABELS : Record<UiSection, string> = {
  ADMIN        : 'Administration',
  PROJECT      : 'Project',
  CMS          : 'Content Management',
  CONFIG_TOOLS : 'Config Tools',
}

export interface UiPageDef {
  id       : string
  label    : string
  section  : UiSection
  enforced : boolean
  /** Backend actions the page needs just to render its data (list/get). */
  apis    ?: readonly UiApiAction[]
  ctas     : Record<string, UiCtaDef>
}

// Plain literal objects (not a builder function) so `as const satisfies`
// preserves each `id` as its literal string type — PermissionRequirement
// needs that to reject typos at compile time, same as PERMISSIONS/BFF_FEATURES.
export const UI_PAGES = {

  /* ---------------- ADMIN — UtilityDrawer "Administration" ---------------- */

  WORKFLOWS : {
    id       : 'ui.page.workflows',
    label    : 'Workflows',
    section  : 'ADMIN',
    enforced : true,
    ctas     : {},
  },

  ENVIRONMENTS : {
    id       : 'ui.page.environments',
    label    : 'Environments',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.ENVIRONMENTS.LIST_ALL],
    ctas     : {
      create : { id : 'ui.cta.environments.create', label : 'Create Environment', apis : [BFF_FEATURES.ENVIRONMENTS.CREATE] },
      edit   : { id : 'ui.cta.environments.edit',   label : 'Edit Environment',   apis : [BFF_FEATURES.ENVIRONMENTS.UPDATE] },
    },
  },

  APPLICATION_SCOPE_MASTER : {
    id       : 'ui.page.application_scope_master',
    label    : 'Application Scope Master',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.APPLICATION_SCOPE.GET_MASTER_DATA],
    ctas     : {
      create_master : { id : 'ui.cta.application_scope_master.create_master', label : 'Create Master Data', apis : [BFF_FEATURES.APPLICATION_SCOPE.CREATE_MASTER_DATA] },
      update_master : { id : 'ui.cta.application_scope_master.update_master', label : 'Update Master Data', apis : [BFF_FEATURES.APPLICATION_SCOPE.UPDATE_MASTER_DATA] },
      delete_master : { id : 'ui.cta.application_scope_master.delete_master', label : 'Delete Master Data', apis : [BFF_FEATURES.APPLICATION_SCOPE.DELETE_MASTER_DATA] },
    },
  },

  GLOBAL_DATA_SCENARIOS : {
    id       : 'ui.page.global_data_scenarios',
    label    : 'Global Data Scenarios',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.CX.GET_DATA_SCENARIOS],
    ctas     : {},
  },

  BRAND_GUIDELINES : {
    id       : 'ui.page.brand_guidelines',
    label    : 'Brand Guidelines',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.CMS_MEDIA.BRAND_GUIDELINES],
    ctas     : {
      save : { id : 'ui.cta.brand_guidelines.save', label : 'Save Brand Guidelines', apis : [BFF_FEATURES.CMS_MEDIA.BRAND_GUIDELINES] },
    },
  },

  PIPELINE_MASTER : {
    id       : 'ui.page.pipeline_master',
    label    : 'Pipeline Master',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.PIPELINE.GET_MASTER_DATA],
    ctas     : {},
  },

  // Strapi-direct pages — no proxypulse `apis`; visibility is granted via the
  // plain ui.* ids below, while Strapi-side enforcement stays legacy if/else.
  CONFIG_REGISTRY : {
    id       : 'ui.page.config_registry',
    label    : 'Config Registry',
    section  : 'ADMIN',
    enforced : true,
    ctas     : {
      create : { id : 'ui.cta.config_registry.create', label : 'New Configuration' },
      save   : { id : 'ui.cta.config_registry.save',   label : 'Save Config' },
      submit : { id : 'ui.cta.config_registry.submit', label : 'Submit Config' },
      reopen : { id : 'ui.cta.config_registry.reopen', label : 'Reopen Config' },
    },
  },

  STRING_REGISTRY : {
    id       : 'ui.page.string_registry',
    label    : 'String Registry',
    section  : 'ADMIN',
    enforced : true,
    ctas     : {
      create  : { id : 'ui.cta.string_registry.create',  label : 'New String' },
      save    : { id : 'ui.cta.string_registry.save',    label : 'Save String' },
      approve : { id : 'ui.cta.string_registry.approve', label : 'Approve String' },
      reject  : { id : 'ui.cta.string_registry.reject',  label : 'Reject String' },
    },
  },

  /** NBI Insights (epic DA-697). Unlike the two registries above this page IS
   *  proxypulse-backed, so it carries `apis` — the list and the catalog are
   *  what it needs just to render. Write, publish and tag actions are gated per
   *  CTA, since proxypulse splits read/write/publish and publish is
   *  CONTENT_PUBLISHERS-only. */
  NBI_INSIGHTS : {
    id       : 'ui.page.nbi_insights',
    label    : 'NBI Insights',
    section  : 'CMS',
    enforced : true,
    apis     : [BFF_FEATURES.INSIGHTS.LIST_OR_CREATE, BFF_FEATURES.INSIGHTS.GET_CATALOG],
    ctas     : {
      /* Same backend row as the list — proxypulse serves GET and POST from one
         view function, so create cannot be granted separately there. The UI
         id below is what actually separates them for a content-reader role. */
      create  : { id : 'ui.cta.nbi_insights.create',  label : 'Create New Insight', apis : [BFF_FEATURES.INSIGHTS.LIST_OR_CREATE] },
      save    : { id : 'ui.cta.nbi_insights.save',    label : 'Save Draft',         apis : [BFF_FEATURES.INSIGHTS.UPDATE_ALL] },
      submit  : { id : 'ui.cta.nbi_insights.submit',  label : 'Save & Submit',      apis : [BFF_FEATURES.INSIGHTS.UPDATE_ALL] },
      publish : { id : 'ui.cta.nbi_insights.publish', label : 'Publish',            apis : [BFF_FEATURES.INSIGHTS.PUBLISH_ALL] },
      unpublish : { id : 'ui.cta.nbi_insights.unpublish', label : 'Unpublish',      apis : [BFF_FEATURES.INSIGHTS.UNPUBLISH_ALL] },
      discard : { id : 'ui.cta.nbi_insights.discard', label : 'Discard',            apis : [BFF_FEATURES.INSIGHTS.DISCARD_ALL_DRAFTS] },
      tag     : { id : 'ui.cta.nbi_insights.tag',     label : 'Tag Recommendation', apis : [BFF_FEATURES.INSIGHTS.TAG_RECOMMENDATIONS] },
    },
  },

  ACCESS_CONTROL : {
    id       : 'ui.page.access_control',
    label    : 'Access Control',
    section  : 'ADMIN',
    enforced : true,
    apis     : [BFF_FEATURES.RBAC.LIST_FEATURE_GROUPS, BFF_FEATURES.RBAC.LIST_ROLES, BFF_FEATURES.RBAC.LIST_USERS],
    ctas     : {
      view_assignments : {
        id    : 'ui.cta.access_control.view_assignments',
        label : 'View Assignments Tab',
      },
      view_access_groups : {
        id    : 'ui.cta.access_control.view_access_groups',
        label : 'View Access Groups Tab',
      },
      view_pages_actions_category : {
        id    : 'ui.cta.access_control.view_pages_actions_category',
        label : 'View Pages & Actions Category',
      },
      view_api_permissions_category : {
        id    : 'ui.cta.access_control.view_api_permissions_category',
        label : 'View API Permissions Category',
      },
      view_group_assignments_category : {
        id    : 'ui.cta.access_control.view_group_assignments_category',
        label : 'View Group Assignments Category',
      },
      view_model_permissions_category : {
        id    : 'ui.cta.access_control.view_model_permissions_category',
        label : 'View Model Permissions Category',
      },
      assign_permissions : {
        id    : 'ui.cta.access_control.assign_permissions',
        label : 'Save Permission Changes',
        apis  : [BFF_FEATURES.RBAC.ATTACH_ROLE_FEATURES, BFF_FEATURES.RBAC.GRANT_ROLE_FEATURE_GROUP, BFF_FEATURES.RBAC.GRANT_USER_FEATURE],
      },
      manage_groups : {
        id    : 'ui.cta.access_control.manage_groups',
        label : 'Manage Access Groups',
        apis  : [BFF_FEATURES.RBAC.CREATE_FEATURE_GROUP, BFF_FEATURES.RBAC.UPDATE_FEATURE_GROUP, BFF_FEATURES.RBAC.DELETE_FEATURE_GROUP],
      },
    },
  },

  /* ---------------- PROJECT — PilotDrawer / project pages ---------------- */

  PROJECTS : {
    id       : 'ui.page.projects',
    label    : 'Projects',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.PROJECTS.LIST],
    ctas     : {
      create  : { id : 'ui.cta.projects.create',  label : 'Create Project',         apis : [BFF_FEATURES.PROJECTS.CREATE] },
      edit    : { id : 'ui.cta.projects.edit',    label : 'Update Project Details', apis : [BFF_FEATURES.PROJECTS.UPDATE] },
      archive : { id : 'ui.cta.projects.archive', label : 'Archive Project',        apis : [BFF_FEATURES.PROJECTS.ARCHIVE] },
    },
  },

  PROJECT_DETAILS : {
    id       : 'ui.page.project_details',
    label    : 'Project Details',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.ASSOCIATED_ENVIRONMENTS.GET],
    ctas     : {
      add_associated_env : { id : 'ui.cta.project_details.add_associated_env', label : 'Add Associated Environment', apis : [BFF_FEATURES.ASSOCIATED_ENVIRONMENTS.ADD] },
    },
  },

  PROJECT_CONFIGS : {
    id       : 'ui.page.project_configs',
    label    : 'Project Configs',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.CONFIG_OPS.GET_PROJECT_CONFIGS],
    ctas     : {
      save : { id : 'ui.cta.project_configs.save', label : 'Save Config',              apis : [BFF_FEATURES.CONFIG_OPS.SAVE_PROJECT_CONFIG] },
      push : { id : 'ui.cta.project_configs.push', label : 'Push Updates to Data Server', apis : [BFF_FEATURES.CONFIG_OPS.PUSH_CONFIGS_TO_DATASERVER] },
    },
  },

  CUSTOMER_DATA_GENERATOR : {
    id       : 'ui.page.customer_data_generator',
    label    : 'Customer Data Generator',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.SELF_SERVE_EMAIL.GET_INGESTED_USERS],
    ctas     : {
      trigger_email_run   : { id : 'ui.cta.customer_data_generator.trigger_email_run',   label : 'Create Email Run',    apis : [BFF_FEATURES.SELF_SERVE_EMAIL.CREATE_RUN] },
      reset_email_session : { id : 'ui.cta.customer_data_generator.reset_email_session', label : 'Reset Email Session', apis : [BFF_FEATURES.SELF_SERVE_EMAIL.RESET_SESSION] },
    },
  },

  UPDATE_USER_METADATA : {
    id       : 'ui.page.update_user_metadata',
    label    : 'Update User Metadata',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.USER_METADATA.GET],
    ctas     : {
      update : {
        id    : 'ui.cta.update_user_metadata.update',
        label : 'Update User Metadata',
        apis  : [BFF_FEATURES.USER_METADATA.UPDATE_SECONDARY_EMAIL, BFF_FEATURES.USER_METADATA.UPDATE_NAME_EMAIL, BFF_FEATURES.USER_METADATA.UPDATE_ADDRESS_ZIPCODE],
      },
    },
  },

  APPLICATION_SCOPE : {
    id       : 'ui.page.application_scope',
    label    : 'Application Scope',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.APPLICATION_SCOPE.GET_PROJECT_APPLICATION_SCOPE],
    ctas     : {
      save : { id : 'ui.cta.application_scope.save', label : 'Save Application Scope', apis : [BFF_FEATURES.APPLICATION_SCOPE.SAVE_PROJECT_APPLICATION_SCOPE] },
    },
  },

  PROJECT_AUDIT : {
    id       : 'ui.page.project_audit',
    label    : 'Project Audit',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.AUDIT.LIST_LOGS],
    ctas     : {},
  },

  PUSH_UPDATES : {
    id       : 'ui.page.push_updates',
    label    : 'Pending Configuration Push',
    section  : 'PROJECT',
    enforced : true,
    apis     : [BFF_FEATURES.CONFIG_OPS.GET_PENDING_UAT_CONFIGS],
    ctas     : {
      push : { id : 'ui.cta.push_updates.push', label : 'Push to Data Server', apis : [BFF_FEATURES.CONFIG_OPS.PUSH_CONFIGS_TO_DATASERVER] },
    },
  },

  /* ---------------- CMS — content management ---------------- */

  CX_EDITOR : {
    id       : 'ui.page.cx_editor',
    label    : 'CX Template Editor',
    section  : 'CMS',
    enforced : true,
    apis     : [BFF_FEATURES.CX.GET_TEMPLATE, BFF_FEATURES.CX.RENDER],
    ctas     : {
      save         : { id : 'ui.cta.cx_editor.save',         label : 'Save Template', apis : [BFF_FEATURES.CX.SAVE] },
      save_strings : { id : 'ui.cta.cx_editor.save_strings', label : 'Save Strings',  apis : [BFF_FEATURES.CX.SAVE_STRINGS] },
      signoff      : { id : 'ui.cta.cx_editor.signoff',      label : 'Sign Off',      apis : [BFF_FEATURES.CX.SIGNOFF] },
      enable_edit  : { id : 'ui.cta.cx_editor.enable_edit',  label : 'Enable Edit',   apis : [BFF_FEATURES.CX.OVERRIDE] },
      upload_image : { id : 'ui.cta.cx_editor.upload_image', label : 'Upload Image',  apis : [BFF_FEATURES.CMS_MEDIA.UPLOAD] },
    },
  },

  DATA_SCENARIOS : {
    id       : 'ui.page.data_scenarios',
    label    : 'Data Scenarios',
    section  : 'CMS',
    enforced : true,
    apis     : [BFF_FEATURES.CX.GET_DATA_SCENARIOS],
    ctas     : {
      create : { id : 'ui.cta.data_scenarios.create', label : 'Create Data Scenario', apis : [BFF_FEATURES.CX.CREATE_DATA_SCENARIO_WITH_PAYLOADS] },
      clone  : { id : 'ui.cta.data_scenarios.clone',  label : 'Clone Data Scenario',  apis : [BFF_FEATURES.CX.CLONE_DATA_SCENARIO] },
      delete : { id : 'ui.cta.data_scenarios.delete', label : 'Delete Data Scenario', apis : [BFF_FEATURES.CX.DELETE_PILOT_SCENARIO] },
    },
  },

  COLOR_PALETTE : {
    id       : 'ui.page.color_palette',
    label    : 'Color Palette',
    section  : 'CMS',
    enforced : true,
    apis     : [BFF_FEATURES.COLOR_PALETTE.GET],
    ctas     : {
      save : { id : 'ui.cta.color_palette.save', label : 'Save Color Palette', apis : [BFF_FEATURES.COLOR_PALETTE.SAVE] },
    },
  },

  MEDIA_CONTENT : {
    id       : 'ui.page.media_content',
    label    : 'Media Content',
    section  : 'CMS',
    enforced : true,
    apis     : [BFF_FEATURES.CMS_MEDIA.GET_IMAGES],
    ctas     : {
      upload  : { id : 'ui.cta.media_content.upload',  label : 'Upload Media',   apis : [BFF_FEATURES.CMS_MEDIA.UPLOAD] },
      promote : { id : 'ui.cta.media_content.promote', label : 'Promote Config', apis : [BFF_FEATURES.CMS_MEDIA.PROMOTE_CONFIG] },
      revert  : { id : 'ui.cta.media_content.revert',  label : 'Revert Config',  apis : [BFF_FEATURES.CMS_MEDIA.REVERT_CONFIG] },
    },
  },

  /* ---------------- CONFIG_TOOLS — system configuration ---------------- */

  DCX_CLUSTERS : {
    id       : 'ui.page.dcx_clusters',
    label    : 'Cluster Configuration',
    section  : 'CONFIG_TOOLS',
    enforced : true,
    apis     : [BFF_FEATURES.CLUSTER_ATTRIBUTES.GET_CLUSTERS],
    ctas     : {
      save_cluster      : { id : 'ui.cta.dcx_clusters.save_cluster',      label : 'Save Cluster',      apis : [BFF_FEATURES.CLUSTER_ATTRIBUTES.CREATE_CLUSTER, BFF_FEATURES.CLUSTER_ATTRIBUTES.UPDATE_CLUSTER] },
      create_attributes : { id : 'ui.cta.dcx_clusters.create_attributes', label : 'Create Attributes', apis : [BFF_FEATURES.CLUSTER_ATTRIBUTES.CREATE_ATTRIBUTES] },
    },
  },

  FEATURE_ACCESS : {
    id       : 'ui.page.feature_access',
    label    : 'Feature Access',
    section  : 'CONFIG_TOOLS',
    enforced : true,
    apis     : [BFF_FEATURES.DCX_CLUSTER_API.GET_FEATURE_METRIC],
    ctas     : {
      feature_resolution : { id : 'ui.cta.feature_access.feature_resolution', label : 'Resolve Feature Access', apis : [BFF_FEATURES.DCX_CLUSTER_API.FEATURE_RESOLUTION] },
    },
  },

  TEMPLATE_PERSONALIZATION : {
    id       : 'ui.page.template_personalization',
    label    : 'Template Personalization',
    section  : 'CONFIG_TOOLS',
    enforced : true,
    apis     : [BFF_FEATURES.DCX_CLUSTER_API.GET_FEATURE_METRIC],
    ctas     : {
      feature_resolution : { id : 'ui.cta.template_personalization.feature_resolution', label : 'Resolve Features',        apis : [BFF_FEATURES.DCX_CLUSTER_API.FEATURE_RESOLUTION] },
      push               : { id : 'ui.cta.template_personalization.push',               label : 'Push Configs & Strings',  apis : [BFF_FEATURES.DCX_CLUSTER_API.PUSH_CONFIGS_STRINGS] },
    },
  },

  CONFIG_COMPARE : {
    id       : 'ui.page.config_compare',
    label    : 'Config Compare',
    section  : 'CONFIG_TOOLS',
    enforced : true,
    apis     : [BFF_FEATURES.CONFIG_TOOLS.GET_COMPARE_RESULT],
    ctas     : {
      compare   : { id : 'ui.cta.config_compare.compare',   label : 'Compare Configs',            apis : [BFF_FEATURES.CONFIG_TOOLS.COMPARE] },
      sync_push : { id : 'ui.cta.config_compare.sync_push', label : 'Push to Target Environment', apis : [BFF_FEATURES.CONFIG_TOOLS.SYNC_PUSH] },
    },
  },

} as const satisfies Record<string, UiPageDef>

/**
 * The single flat proxypulse feature group every granted-at-least-once ui.*
 * feature is filed under (lazy materialization — see RbacApis.ensureUiRegistryGroup
 * and ACCESS_CONTROL_REVAMP_DESIGN.md §4.1). Created on first grant, never by
 * bulk sync.
 */
export const UI_REGISTRY_GROUP_KEY  = 'ui_registry'
export const UI_REGISTRY_GROUP_NAME = 'UI Pages & CTAs'

export type UiPageKey = keyof typeof UI_PAGES
export type UiPageId  = (typeof UI_PAGES)[UiPageKey]['id']

// Mapped (not `C[keyof C] extends {id: infer Id}`) so a page with empty ctas
// contributes `never` — inferring from `never` would resolve Id to `unknown`
// and poison the whole PermissionRequirement union.
type CtaIdOf<Page> = Page extends { ctas : infer C }
  ? { [K in keyof C] : C[K] extends { id : infer Id } ? Id : never }[keyof C]
  : never

/** Every `ui.cta.<page>.<cta>` id across every registered page. */
export type UiCtaId = { [P in UiPageKey] : CtaIdOf<(typeof UI_PAGES)[P]> }[UiPageKey]

export const UI_PAGE_BY_ID : ReadonlyMap<string, UiPageDef> = new Map(
  Object.values(UI_PAGES).map(p => [p.id, p]),
)

/** True for any registry-owned endpoint string — `ui.page.*` or `ui.cta.*`. */
export const isUiEndpoint = (endpoint : string) : boolean => endpoint.startsWith('ui.')

/**
 * Requirement id for a registered+enforced page, or `undefined` when the
 * page id is absent from the registry or still `enforced: false` — callers
 * treat `undefined` as "no additional check", preserving opt-in gating.
 */
export const uiPageRequirement = (pageId ?: UiPageId) : UiPageId | undefined => {
  if (!pageId) return undefined
  const def = UI_PAGE_BY_ID.get(pageId)
  return def?.enforced ? pageId : undefined
}
